/** Tab / extension messaging noise (e.g. Cursor, password managers) — not from this app. */

export function isExtensionTabMessagingNoise(text: string): boolean {
  const t = (text || '').toLowerCase();
  if (!t) return false;
  // Canonical pattern from extensions using Chrome tabs messaging
  if (t.includes('outgoing.message.ready')) return true;
  if (t.includes('tabs:outgoing')) return true;
  if (t.includes('no listener') && (t.includes('tabs:') || t.includes('tabs.'))) return true;
  return false;
}

/** Text from a thrown value / rejection reason (handles nested Error.cause). */
export function rejectionMessage(reason: unknown): string {
  if (reason == null) return '';
  if (typeof reason === 'string') return reason;
  if (reason instanceof Error) {
    const cause = reason.cause;
    const tail = cause != null ? ` ${rejectionMessage(cause)}` : '';
    return `${reason.message}${tail}`;
  }
  if (typeof reason === 'object' && 'message' in reason) {
    return rejectionMessage((reason as { message: unknown }).message);
  }
  try {
    return String(reason);
  } catch {
    return '';
  }
}

function rejectionStack(reason: unknown): string {
  if (reason instanceof Error && reason.stack) return reason.stack;
  if (typeof reason === 'object' && reason !== null && 'stack' in reason) {
    return String((reason as { stack: unknown }).stack ?? '');
  }
  return '';
}

export function rejectionFullText(reason: unknown): string {
  return `${rejectionMessage(reason)} ${rejectionStack(reason)}`.trim();
}

export function isIgnorableExtensionRejection(reason: unknown): boolean {
  return isExtensionTabMessagingNoise(rejectionFullText(reason));
}

function swallowExtensionRejection(event: PromiseRejectionEvent): void {
  if (isIgnorableExtensionRejection(event.reason)) {
    event.preventDefault();
    event.stopImmediatePropagation?.();
  }
}

if (typeof window !== 'undefined') {
  // Capture: run before other listeners (e.g. Sentry) where supported.
  window.addEventListener('unhandledrejection', swallowExtensionRejection, true);

  window.addEventListener(
    'error',
    (ev: ErrorEvent) => {
      const parts = [ev.message, rejectionMessage(ev.error), rejectionStack(ev.error), ev.filename || '']
        .filter(Boolean)
        .join(' ');
      if (isExtensionTabMessagingNoise(parts)) {
        ev.preventDefault();
        ev.stopImmediatePropagation?.();
      }
    },
    true
  );
}
